import { useEffect, useState, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Layout from "@/components/Layout";
import QuickAddLead from "@/components/QuickAddLead";
import { Loading } from "@/components/StateViews";
import api from "@/lib/api";
import { logDev } from "@/lib/ux";

import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Leads from "@/pages/Leads";
import Pipeline from "@/pages/Pipeline";
import Activities from "@/pages/Activities";
import Meetings from "@/pages/Meetings";
import Rules from "@/pages/Rules";
import ImportCenter from "@/pages/ImportCenter";
import Team from "@/pages/Team";
import Settings from "@/pages/Settings";
import AutomationSettings from "@/pages/AutomationSettings";
import Performance from "@/pages/Performance";
import AssignmentReport from "@/pages/AssignmentReport";
import ManagerControlReport from "@/pages/ManagerControlReport";
import SalesLeakageReport from "@/pages/SalesLeakageReport";
import SalespersonPerformanceReport from "@/pages/SalespersonPerformanceReport";
import SourceQualityReport from "@/pages/SourceQualityReport";
import WorkloadAuditReport from "@/pages/WorkloadAuditReport";
import TimesheetsReport from "@/pages/TimesheetsReport";
import DealForms from "@/pages/DealForms";
import OperationsSLA from "@/pages/OperationsSLA";
import PipelineSettings from "@/pages/PipelineSettings";
import Integrations from "@/pages/Integrations";
import ScoringSettings from "@/pages/ScoringSettings";
import RecycleBin from "@/pages/RecycleBin";
import ReviewQueue from "@/pages/ReviewQueue";
import Notifications from "@/pages/Notifications";
import ForceChangePassword from "@/pages/ForceChangePassword";

function ProtectedRoute({ children, roles, perm }) {
  const { user, has } = useAuth();
  if (user === null) return <div className="flex min-h-screen items-center justify-center"><Loading label="Loading workspace..." /></div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  // v1.3 — permission gate. Admin always passes (has() returns true for Admin).
  if (perm && !has(perm)) return <Navigate to="/dashboard" replace />;
  return children;
}

// Redirect a legacy route to its new grouped route while PRESERVING the query
// string, so deep-links like /leads?open=<id> keep working after the nav reorg.
function RedirectWithQuery({ to }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}

function AppShell() {
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [badge, setBadge] = useState(0);
  const [review, setReview] = useState(0);
  const [notif, setNotif] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const { can, user } = useAuth();

  const refreshUsers = useCallback(async () => {
    if (!can("reassign")) return; // only Admin/Manager need the team list
    try { const { data } = await api.get("/users"); setUsers(data.data); } catch (e) { logDev("Failed to load users", e); }
  }, [can]);

  const loadCounts = useCallback(async () => {
    try {
      const { data } = await api.get("/activities");
      setBadge(data.data.badge_count);
    } catch (e) { logDev("Failed to load activity badge", e); }
    if (can("viewAllLeads")) {
      try {
        const { data } = await api.get("/leads", { params: { assigned: "false", page_size: 1 } });
        setNotif(data.data.total);
      } catch (e) { logDev("Failed to load unassigned count", e); }
    }
    if (can("reassign")) {
      try {
        const { data } = await api.get("/review/count");
        setReview(data.data.count);
      } catch (e) { logDev("Failed to load review count", e); }
    }
  }, [can]);

  useEffect(() => { refreshUsers(); loadCounts(); }, [refreshUsers, loadCounts]);

  const bump = () => setRefreshKey((k) => k + 1);

  if (user && user.force_change_password) {
    return <ForceChangePassword />;
  }

  return (
    <Layout
      onQuickAdd={() => setQuickAddOpen(true)}
      badges={{ "/activities": badge, "/leads/review-queue": review }}
      notifCount={notif}
    >
      <Outlet context={{ refreshKey, users, refreshUsers, setBadge, reloadCounts: loadCounts }} />
      <QuickAddLead open={quickAddOpen} onOpenChange={setQuickAddOpen} users={users} onCreated={() => { bump(); loadCounts(); }} />
    </Layout>
  );
}

function App() {
  return (
    <div className="App">
      <Toaster position="top-right" richColors closeButton />
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<Dashboard />} />

              {/* Leads group */}
              <Route path="/leads/all" element={<Leads />} />
              <Route path="/leads/pipeline" element={<Pipeline />} />
              <Route path="/leads/review-queue" element={<ProtectedRoute perm="reports.view_team"><ReviewQueue /></ProtectedRoute>} />

              <Route path="/activities" element={<Activities />} />
              <Route path="/activities/meetings" element={<Meetings />} />

              {/* Automation group */}
              <Route path="/automation/lead-qualification" element={<ProtectedRoute perm="flow_builder.view"><AutomationSettings group="qualification" /></ProtectedRoute>} />
              <Route path="/automation/stage-rules" element={<ProtectedRoute perm="flow_builder.view"><AutomationSettings group="stage" /></ProtectedRoute>} />
              <Route path="/automation/assignment-rules" element={<ProtectedRoute perm="assignment_rules.view"><Rules /></ProtectedRoute>} />
              <Route path="/automation/deal-forms" element={<ProtectedRoute perm="settings.edit"><DealForms /></ProtectedRoute>} />
              <Route path="/automation/operations-sla" element={<ProtectedRoute perm="settings.edit"><OperationsSLA /></ProtectedRoute>} />
              <Route path="/automation/pipeline-settings" element={<ProtectedRoute perm="pipeline_settings.view"><PipelineSettings /></ProtectedRoute>} />
              <Route path="/automation/meta-ads" element={<ProtectedRoute perm="settings.edit"><Integrations /></ProtectedRoute>} />
              <Route path="/automation/scoring" element={<ProtectedRoute perm="scoring.view"><ScoringSettings /></ProtectedRoute>} />

              {/* Reports group */}
              <Route path="/reports/leakage" element={<ProtectedRoute perm="reports.view_team"><SalesLeakageReport /></ProtectedRoute>} />
              <Route path="/reports/salesperson" element={<ProtectedRoute perm="reports.view_team"><SalespersonPerformanceReport /></ProtectedRoute>} />
              <Route path="/reports/source-quality" element={<ProtectedRoute perm="reports.view_team"><SourceQualityReport /></ProtectedRoute>} />
              <Route path="/reports/workload-audit" element={<ProtectedRoute perm="reports.view_team"><WorkloadAuditReport /></ProtectedRoute>} />
              <Route path="/reports/meta-performance" element={<ProtectedRoute perm="reports.view_team"><Performance /></ProtectedRoute>} />
              <Route path="/reports/fairness" element={<ProtectedRoute perm="reports.view_team"><AssignmentReport /></ProtectedRoute>} />
              <Route path="/reports/manager-control" element={<ProtectedRoute perm="reports.view_team"><ManagerControlReport /></ProtectedRoute>} />

              <Route path="/import-center" element={<ProtectedRoute perm="leads.import_csv"><ImportCenter /></ProtectedRoute>} />
              <Route path="/team" element={<ProtectedRoute perm="team.view"><Team /></ProtectedRoute>} />
              <Route path="/team/timesheets" element={<ProtectedRoute perm="timesheets.view"><TimesheetsReport /></ProtectedRoute>} />
              <Route path="/timesheets" element={<Navigate to="/team/timesheets" replace />} />
              <Route path="/recycle-bin" element={<ProtectedRoute perm="recycle_bin.view"><RecycleBin /></ProtectedRoute>} />
              <Route path="/notifications" element={<Notifications />} />
              <Route path="/settings" element={<Settings />} />

              {/* Legacy route redirects (query string preserved) */}
              <Route path="/leads" element={<RedirectWithQuery to="/leads/all" />} />
              <Route path="/pipeline" element={<RedirectWithQuery to="/leads/pipeline" />} />
              <Route path="/review" element={<RedirectWithQuery to="/leads/review-queue" />} />
              <Route path="/performance" element={<RedirectWithQuery to="/reports/meta-performance" />} />
              <Route path="/assignment-report" element={<RedirectWithQuery to="/reports/fairness" />} />
              <Route path="/assignment-rules" element={<RedirectWithQuery to="/automation/assignment-rules" />} />
              <Route path="/automation" element={<RedirectWithQuery to="/automation/lead-qualification" />} />
              <Route path="/deal-forms" element={<RedirectWithQuery to="/automation/deal-forms" />} />
              <Route path="/integrations" element={<RedirectWithQuery to="/automation/meta-ads" />} />
              <Route path="/follow-ups" element={<Navigate to="/activities" replace />} />
            </Route>
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
